/**
 * La pantalla de acceso · `BFF_BO.md` §4
 *
 * Dos caminos y los dos hacen falta: el enlace mágico por defecto, y la
 * contraseña para el personal del club que entra a diario.
 *
 * **El 202 del enlace mágico es siempre igual y el mensaje también.** Si dijera
 * «esa cuenta no existe», cualquiera podría averiguar qué clubes son clientes
 * nuestros probando con el correo del club de al lado. Es una regla de negocio,
 * no una preferencia, y no se ablanda para «mejorar la experiencia».
 *
 * ## «He olvidado la contraseña» · 22-09-2026
 *
 * Faltaba, y era raro de la peor manera: la pantalla ofrecía entrar con contraseña y
 * no ofrecía recuperarla. Quien la olvidaba tenía salida —el enlace mágico entra
 * igual— pero no la encontraba, porque nadie busca «enlace por correo» cuando lo que
 * ha perdido es una contraseña.
 *
 * Responde `202` siempre, **por lo mismo que el enlace mágico**: distinguir las
 * direcciones conocidas convertiría esto en un comprobador de cuentas. Así que el
 * mensaje de después es el mismo exista o no la cuenta.
 */
import { useState } from 'react';
import { api } from '@/shared/api/client';
import { aProblema, type Problema } from '@/shared/api/errores';
import { useSesion } from '@/shared/auth/sesionContexto';
import { Error as PanelError } from '@/shared/ui/Estados';
import { aplicacionDeEsteDominio } from '@/shared/dominios';

type Via = 'enlace' | 'contrasena';

export function Acceso() {
  const { entrar } = useSesion();
  const [via, setVia] = useState<Via>('enlace');
  const [email, setEmail] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [enlaceMandado, setEnlaceMandado] = useState(false);
  /** Se ha pedido restablecer la contraseña. Mismo `202` ciego que el enlace. */
  const [recuperacionMandada, setRecuperacionMandada] = useState(false);

  const pedirEnlace = async () => {
    setEnviando(true);
    setProblema(null);

    const { error } = await api.v1.auth['magic-link'].post({ email });

    setEnviando(false);

    // Un fallo de red sí se cuenta; un «no existe» no, porque el servidor
    // tampoco lo dice.
    if (error) {
      setProblema(aProblema(error));
      return;
    }

    setEnlaceMandado(true);
  };

  const pedirRecuperacion = async () => {
    setEnviando(true);
    setProblema(null);

    const { error } = await api.v1.auth.password.reset.post({ email });

    setEnviando(false);

    if (error) {
      setProblema(aProblema(error));
      return;
    }

    setRecuperacionMandada(true);
  };

  const entrarConContrasena = async () => {
    setEnviando(true);
    setProblema(null);

    const { data, error } = await api.v1.auth.password.post({
      email,
      password: contrasena,
    });

    setEnviando(false);

    if (error || !data) {
      setProblema(aProblema(error));
      return;
    }

    await entrar((data as unknown as { access_token: string }).access_token);
  };

  if (recuperacionMandada) {
    return (
      <main className="acceso">
        <div className="tarjeta">
          <h1>Mira tu correo</h1>
          {/* El mismo texto exista o no la cuenta. Ver la cabecera. */}
          <p>
            Si <b>{email}</b> tiene cuenta, te hemos mandado un enlace para
            poner una contraseña nueva.
          </p>
          <p className="sub">
            Cambiarla cierra todas tus sesiones. Si no lo has pedido tú, puedes
            ignorar el correo: sin abrir el enlace no cambia nada.
          </p>
          <button
            type="button"
            className="btn-plano"
            onClick={() => setRecuperacionMandada(false)}
          >
            Volver
          </button>
        </div>
      </main>
    );
  }

  if (enlaceMandado) {
    return (
      <main className="acceso">
        <div className="tarjeta">
          <h1>Mira tu correo</h1>
          {/* Exactamente el mismo texto exista o no la cuenta. */}
          <p>
            Si <b>{email}</b> tiene cuenta, te hemos mandado un enlace para
            entrar. Caduca en 15 minutos y sirve una sola vez.
          </p>
          <button
            type="button"
            className="btn-plano"
            onClick={() => setEnlaceMandado(false)}
          >
            Probar con otra dirección
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="acceso">
      <div className="tarjeta">
        <h1>Kiap</h1>
        {/* Cada dirección dice qué es antes de pedir la cuenta (02-11-2026). */}
        <p className="sub">
          {aplicacionDeEsteDominio() === 'backoffice'
            ? 'Back office del equipo de Kiap.'
            : 'El panel de tu club.'}
        </p>

        <div className="pestanas" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={via === 'enlace'}
            onClick={() => setVia('enlace')}
          >
            Enlace por correo
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={via === 'contrasena'}
            onClick={() => setVia('contrasena')}
          >
            Contraseña
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void (via === 'enlace' ? pedirEnlace() : entrarConContrasena());
          }}
        >
          <label htmlFor="email">Correo</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {via === 'contrasena' && (
            <>
              <label htmlFor="contrasena">Contraseña</label>
              <input
                id="contrasena"
                type="password"
                autoComplete="current-password"
                required
                value={contrasena}
                onChange={(e) => setContrasena(e.target.value)}
              />
            </>
          )}

          <button type="submit" disabled={enviando}>
            {enviando
              ? 'Un momento…'
              : via === 'enlace'
                ? 'Mandarme el enlace'
                : 'Entrar'}
          </button>
        </form>

        {/*
          Debajo del botón y solo en la vía de contraseña: es donde se busca, y
          ponerlo en la del enlace no significaría nada.
        */}
        {via === 'contrasena' && (
          <button
            type="button"
            className="btn-plano"
            disabled={enviando || email.trim() === ''}
            onClick={() => void pedirRecuperacion()}
            title={
              email.trim() === ''
                ? 'Escribe tu correo y te mandamos el enlace'
                : undefined
            }
          >
            ¿Has olvidado la contraseña?
          </button>
        )}

        {problema && <PanelError problema={problema} />}
      </div>
    </main>
  );
}
